import { SEND_CHANNEL_VERB, declineReasonLabel } from '@fieldops/shared'
import { fileKindLabel, fileMedia, fileSize, fileUrl, filenameCaption } from '../../lib/files.js'
import { formatTime } from '../../lib/format.js'
import type { IconName } from '../Icon.js'
import type { Tone } from '../ui.js'
import type { Job, JobAttachment } from './types.js'

/**
 * Everything that happened on this job, in one column.
 *
 * Assembled here from what `jobs.byId` already returns rather than fetched:
 * there is no activity endpoint, and `AuditEvent` — which is where the real
 * provenance lives — has no read API at all. So this is a reading of the rows
 * themselves, and it is honest about being one. Two consequences worth
 * knowing:
 *
 * - Email on the job comes from `inbox.forJob` and is merged in by the
 *   activity column. SMS still has no job query, so those cards stay typed
 *   and simply do not appear.
 * - A photo has bytes only once the migrator's attachment phase has fetched
 *   them, which during the dual run is true of the window and of nothing else.
 *   `FeedFile.url` is null until then and the tile stays a grey placeholder, so
 *   the feed never asks the browser for an image that does not exist.
 */

export type FeedKind =
  | 'note'
  | 'photo'
  | 'file'
  | 'booking'
  | 'stop'
  | 'time'
  | 'quote'
  | 'invoice'
  | 'status'
  | 'email'
  | 'sms'

/** The filter chips on the mockup, minus Everything which is "no filter". */
export type FeedGroup =
  | 'notes'
  | 'messages'
  | 'files'
  | 'money'
  | 'visits'
  | 'stops'
  | 'status'

export interface FeedFile {
  id: string
  name: string
  /** Three or four letters off the filename: JPG, PDF, HEIC. */
  kind: string
  isImage: boolean
  /** Filename without the extension, which is what the photo grid captions. */
  caption: string
  meta: string
  /** Null until the transfer phase has fetched the bytes from ServiceM8. */
  url: string | null
  /** Tells a clip or a recording from a document. */
  mimeType?: string
}

export interface FeedEntry {
  id: string
  kind: FeedKind
  group: FeedGroup
  icon: IconName
  kindLabel: string
  /** Colours the icon tile, so a kind is recognisable before it is read. */
  tone: Tone
  who: string | null
  at: Date
  text: string
  /** The note / email / SMS body, verbatim. */
  body?: string
  files?: FeedFile[]
  /** The dim line underneath — sizes, reasons, what was typed at the time. */
  meta?: string
  /** Outbound SMS sits on the right in green; inbound on the left. */
  direction?: 'in' | 'out'
  subject?: string
  documentNumber?: string
  /** Minor units. Formatted at the point of drawing. */
  amount?: number
  /**
   * The visit or the calendar event this row *is*, rather than a reading of
   * one. A booking and a stop are drawn from the record itself — the whole
   * card, with its crew and its address — so the column looks the row up
   * instead of the feed flattening it into a sentence.
   */
  visitId?: string
  eventId?: string
  /** The quote a money row is about, so the column can open it. */
  quoteId?: string
}

const KIND: Record<
  FeedKind,
  { kindLabel: string; icon: IconName; group: FeedGroup; tone: Tone }
> = {
  note: { kindLabel: 'Note', icon: 'jobs', group: 'notes', tone: 'neutral' },
  photo: { kindLabel: 'Photos', icon: 'catalog', group: 'files', tone: 'neutral' },
  file: { kindLabel: 'File', icon: 'quotes', group: 'files', tone: 'neutral' },
  booking: { kindLabel: 'Booking', icon: 'schedule', group: 'visits', tone: 'neutral' },
  stop: { kindLabel: 'Stop', icon: 'task', group: 'stops', tone: 'neutral' },
  time: { kindLabel: 'Time', icon: 'today', group: 'status', tone: 'neutral' },
  quote: { kindLabel: 'Quote', icon: 'invoices', group: 'money', tone: 'green' },
  invoice: { kindLabel: 'Invoice', icon: 'invoices', group: 'money', tone: 'green' },
  status: { kindLabel: 'Status', icon: 'today', group: 'status', tone: 'neutral' },
  email: { kindLabel: 'Email', icon: 'mail', group: 'messages', tone: 'outline' },
  sms: { kindLabel: 'SMS', icon: 'phone', group: 'messages', tone: 'outline' },
}

export { KIND as FEED_KIND }

export { fileKindLabel }

export function attachmentFile(attachment: JobAttachment): FeedFile {
  return {
    id: attachment.id,
    name: attachment.filename,
    kind: fileKindLabel(attachment.filename, attachment.mimeType),
    isImage: fileMedia(attachment) === 'image',
    mimeType: attachment.mimeType,
    caption: filenameCaption(attachment.filename),
    meta: attachmentMeta(attachment),
    url: fileUrl(attachment),
  }
}

export function attachmentMeta(attachment: JobAttachment): string {
  return [
    fileKindLabel(attachment.filename, attachment.mimeType),
    fileSize(attachment.sizeBytes),
    attachment.uploadedBy?.name,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Photos taken on one visit arrive as a burst, and a row each buries
 * everything else in the column. They are grouped by uploader and hour, which
 * is close enough to "one trip round the bin store" without inventing a
 * session id nothing records.
 */
function hourBucket(attachment: JobAttachment, prefix: string): string {
  const hour = new Date(attachment.createdAt)
  hour.setMinutes(0, 0, 0)
  return `${prefix}:${attachment.uploadedById ?? 'nobody'}:${hour.toISOString()}`
}

function leadOf(visit: Job['visits'][number]) {
  return visit.assignments.find((assignment) => assignment.isLead) ?? visit.assignments[0]
}

type JobDocumentSend = Job['quotes'][number]['sends'][number]

/**
 * "Quote QUO-64802 v2 emailed to tricia@example.com".
 *
 * The version is not decoration. Two sends of the same quote at different
 * prices are the situation this column exists to make legible, and "sent
 * again" does not distinguish them.
 */
function sendSentence(label: string, number: string, send: JobDocumentSend): string {
  const to = send.toName ?? send.toAddress
  const verb = SEND_CHANNEL_VERB[send.channel]

  return `${label} ${number} v${send.version} ${verb}${to ? ` to ${to}` : ''}`
}

/** The exact PDF that went, as a tile the column can open. */
function renderFile(send: JobDocumentSend): FeedFile {
  const attachment = send.attachment!

  return {
    id: attachment.id,
    name: attachment.filename,
    kind: fileKindLabel(attachment.filename, 'application/pdf'),
    isImage: false,
    caption: `The copy they got — v${send.version}`,
    meta: [SEND_CHANNEL_VERB[send.channel], send.sentBy?.name].filter(Boolean).join(' · '),
    url: fileUrl(attachment),
  }
}

/**
 * The attachments that are a quote or an invoice PDF.
 *
 * Every render the job has ever produced of a billing document, whether it is
 * the current one or a copy some earlier version was sent as. They are pulled
 * out of the file grouping because they already appear in the money rows with
 * the version number and the recipient beside them, and the same PDF listed
 * twice — once as "File", once as "Quote QUO-64802 sent to …" — is noise in
 * the one column that is meant to be a narrative.
 */
function billingRenders(job: Job): Set<string> {
  const ids = new Set<string>()

  for (const document of [...job.quotes, ...job.invoices]) {
    if (document.documentId) ids.add(document.documentId)
    for (const send of document.sends) {
      if (send.attachment) ids.add(send.attachment.id)
    }
  }

  // The signed copy rides on the acceptance row, not as a loose "report".
  for (const quote of job.quotes) {
    if (quote.acceptance?.document) ids.add(quote.acceptance.document.id)
  }

  return ids
}

/** A quote the builder wrote: it has parts or packages. */
export function isProposal(quote: { sections: unknown[]; options: unknown[] }): boolean {
  return quote.sections.length > 0 || quote.options.length > 0
}

/** The signed proposal, once its bytes are stored. */
function signedFile(document: { id: string; checksum: string | null; filename: string }): FeedFile {
  return {
    id: document.id,
    name: document.filename,
    kind: fileKindLabel(document.filename, 'application/pdf'),
    isImage: false,
    caption: filenameCaption(document.filename),
    meta: 'PDF · Signed copy',
    url: fileUrl(document),
  }
}

/** One email as the activity column draws it. `inbox.forJob` is this shape. */
export interface JobEmail {
  id: string
  direction: 'INBOUND' | 'OUTBOUND'
  fromAddress: string
  fromName: string | null
  toAddresses: string[]
  subject: string | null
  bodyText: string | null
  strippedText: string | null
  receivedAt: Date | string
  attachments: {
    attachment: {
      id: string
      filename: string
      mimeType: string
      sizeBytes: number
      checksum: string | null
    }
  }[]
}

/** Sent and received mail, filed at the time it moved. */
export function emailFeedEntries(messages: JobEmail[]): FeedEntry[] {
  return messages.map((message) => {
    const outbound = message.direction === 'OUTBOUND'
    const who = outbound
      ? (message.toAddresses[0] ?? 'customer')
      : (message.fromName ?? message.fromAddress)

    return {
      id: `email:${message.id}`,
      kind: 'email' as const,
      ...KIND.email,
      kindLabel: outbound ? 'Sent' : 'Received',
      who,
      at: new Date(message.receivedAt),
      text: outbound ? `Emailed ${who}` : `Email from ${who}`,
      subject: message.subject ?? undefined,
      body: message.strippedText ?? message.bodyText ?? undefined,
      files: message.attachments.map(({ attachment }) => ({
        id: attachment.id,
        name: attachment.filename,
        kind: fileKindLabel(attachment.filename, attachment.mimeType),
        isImage: fileMedia(attachment) === 'image',
        mimeType: attachment.mimeType,
        caption: filenameCaption(attachment.filename),
        meta: fileKindLabel(attachment.filename, attachment.mimeType),
        url: fileUrl(attachment),
      })),
    }
  })
}

export function buildFeed(job: Job): FeedEntry[] {
  const entries: FeedEntry[] = []
  const renders = billingRenders(job)

  for (const note of job.notes) {
    entries.push({
      id: `note:${note.id}`,
      kind: 'note',
      ...KIND.note,
      kindLabel: note.isPinned ? 'Pinned' : KIND.note.kindLabel,
      who: note.author?.name ?? null,
      at: note.createdAt,
      text: note.isInternal ? 'Left a note on the job.' : 'Added a note the customer can see.',
      body: note.body,
    })
  }

  const buckets = new Map<string, JobAttachment[]>()
  for (const attachment of job.attachments) {
    if (renders.has(attachment.id)) continue

    const file = attachmentFile(attachment)
    const key = file.isImage ? hourBucket(attachment, 'photo') : hourBucket(attachment, 'file')
    buckets.set(key, [...(buckets.get(key) ?? []), attachment])
  }

  for (const [key, group] of buckets) {
    const first = group[0]!
    const files = group.map(attachmentFile)
    const images = files[0]?.isImage ?? false

    /*
     * "Forms completed on site" describes exactly one thing: the PDFs
     * ServiceM8 produced from its own forms, which the migrator lands as
     * `DOCUMENT`. It used to be said about any all-PDF bucket, and once
     * FieldOps started rendering its own documents that meant a stored quote
     * appeared in the feed as a completed form. A `GENERATED_PDF` that is not
     * a billing render is a report this product drew, so it says so.
     */
    const forms = group.every((row) => row.kind === 'DOCUMENT' && row.mimeType === 'application/pdf')
    const reports = group.every((row) => row.kind === 'GENERATED_PDF')

    entries.push({
      id: `attachment:${key}`,
      kind: images ? 'photo' : 'file',
      ...KIND[images ? 'photo' : 'file'],
      who: first.uploadedBy?.name ?? null,
      at: first.createdAt,
      text: images
        ? group.length === 1
          ? '1 photo from site'
          : `${group.length} photos from site`
        : forms
          ? 'Forms completed on site'
          : reports
            ? group.length === 1
              ? 'Report ready'
              : `${group.length} reports ready`
            : group.length === 1
              ? `Attached ${first.filename}.`
              : `Attached ${group.length} files.`,
      files,
    })
  }

  for (const visit of job.visits) {
    const lead = leadOf(visit)
    const slot = formatTime(visit.startAt)

    /*
     * The booking itself, filed at the time the van is due rather than at the
     * time dispatch typed it in. That is what puts a visit still to happen
     * above the now-marker and a visit already made down in the history where
     * it belongs, instead of pinning one card to the top of the column
     * whichever side of today it falls on.
     */
    entries.push({
      id: `booking:${visit.id}`,
      kind: 'booking',
      ...KIND.booking,
      who: lead?.user.name ?? null,
      at: visit.startAt,
      text: lead ? `${slot} with ${lead.user.name}` : `${slot}, nobody assigned`,
      visitId: visit.id,
    })

    entries.push({
      id: `assigned:${visit.id}`,
      kind: 'status',
      ...KIND.status,
      who: null,
      at: visit.createdAt ?? visit.startAt,
      text: lead ? `Assigned to ${lead.user.name} for ${slot}` : `Booked for ${slot}`,
      visitId: visit.id,
    })

    if (visit.status === 'EN_ROUTE' && !visit.arrivedAt) {
      entries.push({
        id: `enroute:${visit.id}`,
        kind: 'status',
        ...KIND.status,
        who: lead?.user.name ?? null,
        at: visit.updatedAt ?? visit.startAt,
        text: 'On my way',
      })
    }

    if (visit.arrivedAt) {
      entries.push({
        id: `arrived:${visit.id}`,
        kind: 'status',
        ...KIND.status,
        who: lead?.user.name ?? null,
        at: visit.arrivedAt,
        text: "I've arrived",
      })
    }

    if (visit.departedAt) {
      entries.push({
        id: `departed:${visit.id}`,
        kind: 'status',
        ...KIND.status,
        who: lead?.user.name ?? null,
        at: visit.departedAt,
        text: 'Finished here',
      })
    }
  }

  /*
   * Pickups, key collections and the rest of the non-job time booked against
   * this job. A stop is a `CalendarEvent` and never a visit (decision 67), so
   * it carries its own chip rather than joining the bookings.
   */
  for (const stop of job.calendarEvents ?? []) {
    entries.push({
      id: `stop:${stop.id}`,
      kind: 'stop',
      ...KIND.stop,
      who: stop.attendees[0]?.user.name ?? null,
      at: stop.startAt,
      text: stop.title,
      eventId: stop.id,
    })
  }

  for (const entry of job.timeEntries) {
    if (!entry.endAt) continue

    const minutes = Math.round((entry.endAt.getTime() - entry.startAt.getTime()) / 60_000)
    entries.push({
      id: `time:${entry.id}`,
      kind: 'status',
      ...KIND.status,
      who: entry.user.name,
      at: entry.endAt,
      text: `Logged ${minutes} minutes`,
      ...(entry.notes ? { meta: entry.notes } : {}),
    })
  }

  for (const quote of job.quotes) {
    const proposal = isProposal(quote)
    const label = proposal ? 'Proposal' : 'Quote'

    /*
     * A proposal is on the job from the moment it is started, not only once it
     * has gone out: the office writes one over a day or two and comes back to
     * it from here. A plain quote still has no "drafted" row (rule 33) — the
     * ServiceM8 library would bury the column in them.
     */
    if (proposal) {
      entries.push({
        id: `proposal:${quote.id}`,
        kind: 'quote',
        ...KIND.quote,
        kindLabel: 'Proposal',
        who: null,
        at: quote.createdAt,
        text:
          quote.status === 'DRAFT'
            ? `Proposal${quote.version > 1 ? ` v${quote.version}` : ''} started, not sent yet`
            : `Proposal${quote.version > 1 ? ` v${quote.version}` : ''} written`,
        documentNumber: quote.number,
        amount: quote.totalIncTax,
        quoteId: quote.id,
      })
    }

    /*
     * One row per send, not one row for `sentAt`.
     *
     * "Quote QUO-64802 sent" said nothing about which version, who received
     * it or who pressed the button, and a resend produced no row at all
     * because the column was reading a single date. The version number is the
     * part that matters when somebody rings up about a figure.
     */
    for (const send of quote.sends) {
      entries.push({
        id: `quote-send:${quote.id}:${send.version}`,
        kind: 'quote',
        ...KIND.quote,
        who: send.sentBy?.name ?? null,
        at: send.sentAt,
        text: sendSentence(label, quote.number, send),
        documentNumber: quote.number,
        amount: quote.totalIncTax,
        quoteId: quote.id,
        ...(send.note ? { meta: send.note } : {}),
        ...(send.attachment ? { files: [renderFile(send)] } : {}),
      })
    }

    // A quote sent before send tracking existed, or imported from ServiceM8.
    // The date is all there is, and it is still worth a row.
    if (quote.sentAt && quote.sends.length === 0) {
      entries.push({
        id: `quote-sent:${quote.id}`,
        kind: 'quote',
        ...KIND.quote,
        who: null,
        at: quote.sentAt,
        text: `${label} ${quote.number} sent`,
        documentNumber: quote.number,
        amount: quote.totalIncTax,
        quoteId: quote.id,
      })
    }

    // The first time the customer opened their link. One row, not one per
    // visit: how often they came back is the meta, which is what the office
    // reads it for — a customer on their fourth look is ready for a call.
    const opened = quote.shareLinks[0]
    if (opened?.firstViewedAt) {
      const views = quote.shareLinks.reduce((sum, link) => sum + link.viewCount, 0)
      entries.push({
        id: `quote-viewed:${quote.id}`,
        kind: 'quote',
        ...KIND.quote,
        who: null,
        at: opened.firstViewedAt,
        text: `${label} ${quote.number} opened by the customer`,
        documentNumber: quote.number,
        quoteId: quote.id,
        ...(views > 1 ? { meta: `Opened ${views} times` } : {}),
      })
    }

    if (quote.respondedAt) {
      const acceptance = quote.status === 'ACCEPTED' ? quote.acceptance : null
      const choice = acceptance
        ? [
            acceptance.option?.name ?? null,
            acceptance.addOnLineIds.length > 0
              ? `${acceptance.addOnLineIds.length} extra${acceptance.addOnLineIds.length === 1 ? '' : 's'}`
              : null,
          ]
            .filter(Boolean)
            .join(' + ')
        : ''
      const declined = [declineReasonLabel(quote.declineCategory), quote.declineReason]
        .filter(Boolean)
        .join(' — ')

      // A yes is a money card carrying the signed copy; a no stays a status
      // line. `document` is null until the render after acceptance lands.
      const signed = acceptance?.document?.checksum ? acceptance.document : null

      entries.push({
        id: `quote-response:${quote.id}`,
        ...(acceptance ? { kind: 'quote' as const, ...KIND.quote } : { kind: 'status' as const, ...KIND.status }),
        who: acceptance?.signedName ?? quote.acceptedByName,
        at: acceptance?.acceptedAt ?? quote.respondedAt,
        text:
          quote.status === 'ACCEPTED'
            ? `${label} ${quote.number} accepted${acceptance?.channel === 'ONLINE' ? ' and signed online' : ''}`
            : `${label} ${quote.number} declined`,
        quoteId: quote.id,
        ...(acceptance ? { amount: acceptance.acceptedTotalIncTax, documentNumber: quote.number } : {}),
        ...(choice ? { meta: choice } : declined ? { meta: declined } : {}),
        ...(signed ? { files: [signedFile(signed)] } : {}),
      })
    }
  }

  for (const invoice of job.invoices) {
    if (invoice.issuedAt) {
      entries.push({
        id: `invoice:${invoice.id}`,
        kind: 'invoice',
        ...KIND.invoice,
        who: null,
        at: invoice.issuedAt,
        text:
          invoice.status === 'DRAFT'
            ? 'Invoice drafted, not sent'
            : `Invoice ${invoice.number} issued`,
        documentNumber: invoice.number,
        amount: invoice.totalIncTax,
      })
    }

    // Issuing and sending are different acts (decision 72), so they are
    // different rows. An invoice can be issued in March and chased in May.
    for (const send of invoice.sends) {
      entries.push({
        id: `invoice-send:${invoice.id}:${send.version}`,
        kind: 'invoice',
        ...KIND.invoice,
        who: send.sentBy?.name ?? null,
        at: send.sentAt,
        text: sendSentence('Invoice', invoice.number, send),
        documentNumber: invoice.number,
        amount: invoice.totalIncTax,
        ...(send.note ? { meta: send.note } : {}),
        ...(send.attachment ? { files: [renderFile(send)] } : {}),
      })
    }

    // The first time the customer opened an invoice's link, whichever send
    // it came in. One row, like a quote's. Decision 88.
    const links = invoice.sends.flatMap((send) => (send.invoiceShareLink ? [send.invoiceShareLink] : []))
    const firstViewed = links
      .flatMap((link) => (link.firstViewedAt ? [link.firstViewedAt] : []))
      .sort((a, b) => a.getTime() - b.getTime())[0]
    if (firstViewed) {
      const views = links.reduce((sum, link) => sum + link.viewCount, 0)
      entries.push({
        id: `invoice-viewed:${invoice.id}`,
        kind: 'invoice',
        ...KIND.invoice,
        who: null,
        at: firstViewed,
        text: `Invoice ${invoice.number} opened by the customer`,
        documentNumber: invoice.number,
        ...(views > 1 ? { meta: `Opened ${views} times` } : {}),
      })
    }
  }

  if (job.completedAt) {
    entries.push({
      id: 'completed',
      kind: 'status',
      ...KIND.status,
      who: null,
      at: job.completedAt,
      text: 'Job completed',
    })
  }

  if (job.cancelledAt) {
    entries.push({
      id: 'cancelled',
      kind: 'status',
      ...KIND.status,
      who: null,
      at: job.cancelledAt,
      text: 'Job marked unsuccessful',
      ...(job.cancelledReason ? { meta: job.cancelledReason } : {}),
    })
  }

  entries.push({
    id: 'created',
    kind: 'status',
    ...KIND.status,
    who: null,
    at: job.createdAt,
    text: 'Job created',
  })

  return entries.sort((a, b) => b.at.getTime() - a.at.getTime())
}
